import i18n from '@/i18n'

export interface PhoneValidationResult {
  isValid: boolean
  error?: string
}

export const NUMEROS_URGENCE = ['911', '933']

export function validatePhoneNumber(phone: string): PhoneValidationResult {
  const trimmed = phone.trim()
  // Messages in the language of the screen (i18n), not French only
  if (!trimmed) return { isValid: false, error: i18n.t('telephone.requis') }

  const cleaned = trimmed.replace(/[\s()-]/g, '')

  // Les secours (911) et le test d'adresse 911 (933) : toujours composables.
  if (NUMEROS_URGENCE.includes(cleaned)) return { isValid: true }

  if (cleaned.startsWith('+')) {
    const digitsOnly = cleaned.slice(1)
    if (!/^\d+$/.test(digitsOnly)) return { isValid: false, error: i18n.t('telephone.chiffresApresPlus') }
    if (digitsOnly.length < 8) return { isValid: false, error: i18n.t('telephone.tropCourt') }
    if (digitsOnly.length > 15) return { isValid: false, error: i18n.t('telephone.tropLong') }
    return { isValid: true }
  }

  if (!/^\d+$/.test(cleaned)) return { isValid: false, error: i18n.t('telephone.chiffres') }
  if (cleaned.length === 10) return { isValid: true }
  if (cleaned.length === 11 && cleaned.startsWith('1')) return { isValid: true }

  return { isValid: false, error: i18n.t('telephone.exemple') }
}

/**
 * Normalise un numéro de téléphone vers le format E.164 (`+XXXXXXXXXXXX`).
 *
 * Règles :
 * - Espaces, tirets, parenthèses, points sont retirés
 * - `+...` → conservé tel quel si valide
 * - 10 chiffres → assume Canada/US, préfixe `+1`
 * - 11 chiffres commençant par 1 → préfixe `+`
 *
 * Retourne `null` si le numéro ne peut pas être normalisé.
 */
export function normalizeToE164(
  phone: string,
  defaultCountry: 'CA' | 'US' = 'CA',
): string | null {
  const trimmed = phone.trim()
  if (!trimmed) return null
  const cleaned = trimmed.replace(/[\s()\-.]/g, '')

  if (cleaned.startsWith('+')) {
    const digits = cleaned.slice(1)
    if (!/^\d{8,15}$/.test(digits)) return null
    return `+${digits}`
  }

  if (!/^\d+$/.test(cleaned)) return null

  if (cleaned.length === 10 && (defaultCountry === 'CA' || defaultCountry === 'US')) {
    return `+1${cleaned}`
  }
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+${cleaned}`
  }
  return null
}

/** Affichage lisible d'un numéro nord-américain : +1 438 555-0001. Sinon, tel quel. */
export function formatPhoneDisplay(phone: string): string {
  const digits = (phone || '').replace(/\D/g, '')
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 ${digits.slice(1, 4)} ${digits.slice(4, 7)}-${digits.slice(7)}`
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return phone
}
